import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Card, CircularProgress, TextField, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { DataGrid, enUS, frFR, GridCellParams, GridColDef, GridToolbar } from '@mui/x-data-grid';
import moment from 'moment';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import 'moment/locale/fr';
import 'moment/locale/en-gb';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AuthHeader } from '../../../../utils/helpers/auth-headers';
import './HistoryList.scss';
import { AlertSeverity } from '../../../../utils/enum/Alerts';
import { setAlertData } from '../../../../stores/alertStore';
import { PageRowsOptions } from '../../../../models/DataGridConf';
import { RootState } from '../../../../stores/globalStore';
import { setConnectedUser } from '../../../../stores/authenticationStore';

interface LiveMeeting {
  meetingName: string;
  participantCount: number;
  start: string;
  uid: string;
  worstQuality: string;
}

export default function HistoryList() {
  const { t } = useTranslation();

  const i18nLang = i18next.language;
  const [gridLang, setGridLang] = useState(frFR);

  const connectedUser = useSelector((state: RootState) => state.authentication.connectedUser);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setLoading] = useState<boolean>(true);
  const path = useLocation().pathname;
  const [pathName, setPathName] = useState<string>(path);
  const paramsRoute = useParams();
  const [pageSize, setPageSize] = useState<number>(connectedUser.prefs?.nbRow ? connectedUser.prefs.nbRow : 25);
  const pageRowsOptions = PageRowsOptions;
  const [historyMeetings, setHistoryMeetings] = useState<LiveMeeting[]>([]);
  const [selectionModel, setSelectionModel] = useState<any[]>([]);

  const currentDate = new Date();
  const [dateFrom, setDateFrom] = useState<Date | null>(new Date(new Date(currentDate.setDate(currentDate.getDate() - 7)).setHours(0, 0, 0, 0)));
  const [dateTo, setDateTo] = useState<Date | null>(new Date(new Date().setHours(23, 59, 59, 999)));
  const [locale, setLocale] = useState('fr');

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      minWidth: 90,
      flex: 1,
      hide: true
    },
    {
      field: 'name',
      renderHeader: () => (
        <Tooltip title={t('HistoryList.table.name') as string} arrow>
          <span className="strong-header">{t('HistoryList.table.name')}</span>
        </Tooltip>
      ),
      minWidth: 300,
      type: 'string',
      flex: 1
    },
    {
      field: 'duration',
      renderHeader: () => (
        <Tooltip title={t('HistoryList.table.duration') as string} arrow>
          <span className="strong-header">{t('HistoryList.table.duration')}</span>
        </Tooltip>
      ),
      minWidth: 120,
      maxWidth: 130,
      type: 'dateTime',
      flex: 1,
      renderCell: (params) => {
        let d = params.row.duration;
        if (d <= 0) {
          return 0;
        }
        d = Number(d);
        const h = Math.floor(d / 3600);
        const m = Math.floor((d % 3600) / 60);
        const s = Math.floor((d % 3600) % 60);
        const hDisplay = h > 0 ? h + (h === 1 ? ' h, ' : ' h, ') : '';
        const mDisplay = m > 0 ? m + (m === 1 ? ' m, ' : ' m, ') : '';
        const sDisplay = s > 0 ? s + (s === 1 ? ' s' : ' s') : '';
        return hDisplay + mDisplay + sDisplay;
      }
    },
    {
      field: 'startTime',
      renderHeader: () => (
        <Tooltip title={t('HistoryList.table.startTime') as string} arrow>
          <span className="strong-header">{t('HistoryList.table.startTime')}</span>
        </Tooltip>
      ),
      minWidth: 170,
      maxWidth: 170,
      type: 'dateTime',
      flex: 1,
      renderCell: ((params) => {
        const date = new Date(params.row.startTime).toLocaleString(i18nLang);
        return (<p>{date}</p>);
      })
    },
    {
      field: 'endTime',
      renderHeader: () => (
        <Tooltip title={t('HistoryList.table.endTime') as string} arrow>
          <span className="strong-header">{t('HistoryList.table.endTime')}</span>
        </Tooltip>
      ),
      minWidth: 170,
      maxWidth: 170,
      type: 'dateTime',
      flex: 1,
      renderCell: ((params) => {
        const date = new Date(params.row.endTime).toLocaleString(i18nLang);
        return (<p>{date}</p>);
      })

    },
    {
      field: 'nbParticipants',
      renderHeader: () => (
        <Tooltip title={t('HistoryList.table.participants') as string} arrow>
          <span className="strong-header">{t('HistoryList.table.participants')}</span>
        </Tooltip>
      ),
      minWidth: 60,
      maxWidth: 100,
      flex: 1
    }
  ];

  interface GridRowDefPexip {
    id: string,
    name: string,
    duration: string,
    startTime: string,
    endTime: string
    nbParticipants: number
  }

  function buildRows(data: any) {
    const rows: GridRowDefPexip[] = [];
    for (let i = 0; i < data.length; i += 1) {
      const row = {
        id: data[i]._id,
        name: data[i].name,
        duration: data[i].duration,
        startTime: data[i].startTime,
        endTime: data[i].endTime,
        nbParticipants: data[i].participantCount
      };
      rows.push(row);
    }
    return rows;
  }

  useEffect(() => {
    axios.get(`${window._env_.REACT_APP_API_URI}/pexipInstance/${paramsRoute.instanceId}/historyMeeting?startTime(gte)=${dateFrom?.toISOString()}&endTime(lte)=${dateTo?.toISOString()}&limit=10000`, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then((response) => setHistoryMeetings(response.data.data))
      .catch((error) => {
        if (error.response) {
          const err = error.response;
          console.error(err);
          dispatch(setAlertData({
            message: err.data.message,
            severity: AlertSeverity.ERROR,
            open: true,
          }));
        }
        console.error(error);
      })
      .then(() => setLoading(false));
  }, [paramsRoute, dateFrom, dateTo]);

  useEffect(() => {
    if (!path.includes('/history/')) {
      setSelectionModel([]);
    } else {
      setSelectionModel([paramsRoute.meetingId]);
    }
  }, [pathName]);

  useEffect(() => {
    setPathName(path);
  }, [path]);

  useEffect(() => {
    if (i18nLang === 'en') {
      setGridLang(enUS);
      setLocale('en');
      moment.updateLocale('en', { week: { dow: 1 } });
    } else {
      setGridLang(frFR);
      setLocale('fr');
      moment.updateLocale('fr', { week: { dow: 1 } });
    }
  }, [i18nLang]);

  const handleDateFrom = (newValue: Date) => {
    if (newValue && dateTo && newValue > dateTo) {
      setDateFrom(new Date(new Date(dateTo).setHours(0, 0, 0, 0)));
    } else {
      setDateFrom(new Date(new Date(newValue).setHours(0, 0, 0, 0)));
    }
  };

  const handleDateTo = (newValue: Date) => {
    if (newValue && dateFrom && newValue < dateFrom) {
      setDateTo(new Date(new Date(dateFrom).setHours(23, 59, 59, 999)));
    } else {
      setDateTo(new Date(new Date(newValue).setHours(23, 59, 59, 999)));
    }
  };

  const updateNbRowUser = (updateNbRow: number) => {  
    const updatePrefs = { ...connectedUser.prefs };
    updatePrefs.nbRow = updateNbRow;
    const modifyPrefs = {
      prefs: updatePrefs
    };
    axios.post(`${window._env_.REACT_APP_API_URI}/user/prefs`, modifyPrefs, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then((response) => {
        dispatch(setConnectedUser({ connectedUser: response.data.data }));
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const changePageSize = (newPage: number) => {
    setPageSize(newPage);
    updateNbRowUser(newPage);
  };

  return (
    <div>

      <div className={pathName.includes('/history/') ? 'history-bloc outlet-open' : 'hisory-bloc'}>
        <Card className="category-container">
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (

            <div style={{ height: 707 }}>
              <div className="category-container date-picker">
                <LocalizationProvider dateAdapter={AdapterMoment} locale={locale}>
                  <DatePicker
                    label={t('HistoryList.from')}
                    value={dateFrom}
                    onChange={(newValue) => {
                      handleDateFrom(newValue as Date);
                    }}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterMoment} locale={locale}>
                  <DatePicker
                    label={t('HistoryList.to')}
                    value={dateTo}
                    onChange={(newValue) => {
                      handleDateTo(newValue as Date);
                    }}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
              </div>
              <DataGrid
                style={{ height: 651 }}
                rows={buildRows(historyMeetings)}
                columns={columns}
                pageSize={pageSize}
                rowsPerPageOptions={pageRowsOptions}
                onPageSizeChange={(newPage) => changePageSize(newPage)}
                components={{ Toolbar: GridToolbar }}
                pagination
                onCellClick={(params: GridCellParams) => {
                  navigate(`./${params.row.id}`);
                }}
                getRowClassName={() => 'pointer'}
                hideFooterSelectedRowCount
                onSelectionModelChange={setSelectionModel}
                selectionModel={selectionModel}
                localeText={gridLang.components.MuiDataGrid.defaultProps.localeText}
                density="compact"
              />
            </div>
          )}
        </Card>
        <div className="category-container">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
